import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import { currencies } from '../../../utils/functions';

/**
 * Reusable ISO 4217 currency picker (tag matter-economics, US #2236/#2235).
 *
 * Wraps the shared `Dropdown` over the canonical `currencies` list so the same
 * control is used by the matter form, org settings, rate overrides, time
 * entries and outcome capture. Searchable; options read "EUR - Euro (€)".
 */
interface CurrencyPickerProps {
  /** Selected ISO 4217 code, e.g. "EUR". */
  value?: string;
  onChange: (currency: string) => void;
  id?: string;
  label?: string;
  /** Muted line under the label - the same slot every other field has. */
  description?: string;
  /** Muted line under the control. */
  helpText?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  compact?: boolean;
  fullWidth?: boolean;
  infoText?: string;
}

const CurrencyPicker: React.FC<CurrencyPickerProps> = ({
  value,
  onChange,
  id = 'currency-picker',
  label,
  description,
  helpText,
  placeholder,
  error,
  required,
  className,
  compact,
  fullWidth,
  infoText,
}) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      currencies.map((c) => ({
        value: c.value,
        label: `${c.value} - ${t(c.label)} (${c.symbol})`,
      })),
    [t],
  );

  return (
    <Dropdown
      id={id}
      label={label}
      description={description}
      helpText={helpText}
      options={options}
      placeholder={placeholder ?? t('Select currency')}
      value={value ? value.toUpperCase() : value}
      onChange={(selected) => onChange(Array.isArray(selected) ? selected[0] : selected)}
      error={error}
      required={required}
      className={className}
      compact={compact}
      fullWidth={fullWidth}
      infoText={infoText}
    />
  );
};

export default CurrencyPicker;
